import React, { useState, useEffect } from 'react';
import { TripData } from '../data/tripData';
import { useLanguage } from '../hooks/useLanguage';

interface ItineraryNavigationDotsProps {
  tripData: TripData;
}

export default function ItineraryNavigationDots({ tripData }: ItineraryNavigationDotsProps) {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const scrollPosition = window.scrollY + windowHeight / 2;
      
      // Find which day group we're currently viewing
      let currentGroup = 0;
      
      // Check for each day group
      const dayGroups = [1, 2, 3, '4-6', 7];
      
      for (let i = 0; i < dayGroups.length; i++) {
        const dayElement = document.getElementById(`day-header-${dayGroups[i]}`);
        if (dayElement) {
          const elementTop = dayElement.offsetTop;
          const elementBottom = elementTop + dayElement.offsetHeight;
          
          if (scrollPosition >= elementTop && scrollPosition <= elementBottom) {
            currentGroup = i;
            break;
          } else if (scrollPosition > elementTop) {
            currentGroup = i; // We're past this element, so we're in this group
          }
        }
      }
      
      setActiveSection(currentGroup);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Define the day groups as specified: 1, 2, 3, 4-6, 7
  const dayGroups = [
    { id: 1, label: t('trips.summerTrip.itinerary.itineraryNavigation.day1'), targetDay: 1 },
    { id: 2, label: t('trips.summerTrip.itinerary.itineraryNavigation.day2'), targetDay: 2 },
    { id: 3, label: t('trips.summerTrip.itinerary.itineraryNavigation.day3'), targetDay: 3 },
    { id: 4, label: t('trips.summerTrip.itinerary.itineraryNavigation.days4to6'), targetDay: '4-6' },
    { id: 5, label: t('trips.summerTrip.itinerary.itineraryNavigation.day7'), targetDay: 7 }
  ];

  return (
    <div className="fixed top-1/2 right-4 transform -translate-y-1/2 z-[100] hidden lg:block">
      <div className="flex flex-col gap-2 items-end">
        {dayGroups.map((group, index) => (
          <div key={group.id} className="flex items-center gap-2">
            {/* Label with arrow pointing to dot */}
            {activeSection === index && (
              <div className="relative">
                <div className="bg-gray-800 bg-opacity-80 text-white text-xs font-medium px-2 py-1 rounded shadow-lg">
                  {group.label}
                </div>
                {/* Arrow pointing to the dot */}
                <div className="absolute top-1/2 -right-1 transform -translate-y-1/2">
                  <div className="w-0 h-0 border-l-4 border-l-gray-800 border-t-2 border-b-2 border-t-transparent border-b-transparent"></div>
                </div>
              </div>
            )}
            
            {/* Dot */}
            <button
              onClick={() => {
                const element = document.getElementById(`day-header-${group.targetDay}`)
                if (element) {
                  element.scrollIntoView({ behavior: "smooth" })
                } else {
                  // Fallback: try to find the first element of the group
                  if (group.targetDay === '4-6') {
                    const fallbackElement = document.getElementById('day-header-4') || 
                                          document.querySelector('[id*="day-header-4"]') ||
                                          document.querySelector('[id*="day-header-5"]') ||
                                          document.querySelector('[id*="day-header-6"]')
                    fallbackElement?.scrollIntoView({ behavior: "smooth" })
                  }
                }
              }}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeSection === index
                  ? "bg-blue-600 scale-125"
                  : "bg-gray-400 hover:bg-gray-600"
              }`}
              title={group.label}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
